import { useCallback, useEffect, useMemo, useRef } from 'react';
import { connect, useDispatch } from 'react-redux';
import { Form } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import { FieldArray, FieldArrayRenderProps } from 'react-final-form-arrays';
import { v4 as uuidv4 } from 'uuid';
import type { Dispatch } from 'redux';

import { KnmReasonChangeTypeOperationEnum } from 'APIPackage/CatalogsModule/enums/KnmReasonChangeTypeOperationEnum';
import { catalogGet } from 'CorePackage/CatalogsModule/actions/catalogsActions';
import { CommonCatalogsNamesEnum } from 'CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { IKnmReasonChangeTypeSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmReasonChangeTypeSelectOption';
import { IKnmTypeSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmTypeSelectOption';
import { geCatalogsKnmPlanReasonChangeTypesSelectOptions } from 'CorePackage/CatalogsModule/selectors/catalogsSelectors';
import { IDictionaryKnmKindSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryKnmKindSelectOption';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { makeRequired } from 'CorePackage/MainModule/validators/isRequired';
import { KnmDocuments } from 'KnmPackage/KnmDocumentsModule/components/KnmDocuments';
import { IKnmErknmFormChangePlanReason } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormChangePlanReason';
import { knmCheckChanges, knmSetIsChangePlanReasonActive } from 'KnmPackage/MainModule/actions/knmActions';
import { Button } from 'UIPackage/MainModule/components/Button';
import { CloseButton } from 'UIPackage/MainModule/components/CloseButton';
import { Modal } from 'UIPackage/ModalModule/components/Modal';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { ModalHeader } from 'UIPackage/ModalModule/components/ModalHeader';
import { ModalBody } from 'UIPackage/ModalModule/components/ModalBody';
import { ModalActions } from 'UIPackage/ModalModule/components/ModalActions';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { setIsNpaStructureChanged } from 'KnmPackage/RequirementsNpaModalModule/actions/requirementsNpaActions';

import { getKnmFormRef } from '../selectors/knmSelectors';
import { IKnmFormRef } from '../interfaces/IKnmFormRef';
import styles from './ChangeKnmModal.module.scss';
import { ChangeKnmModalFormRenderProps } from '../types/ChangeKnmModalFormRenderProps';
import { IChangeKnmModalForm } from '../interfaces/IChangeKnmModalForm';
import { IKnmErknmFormObject } from '../../../KnmErknmPackage/MainModule/interfaces/IKnmErknmFormObject';

interface IChangeKnmModalProps {
  visible: boolean;
  typeFieldValue: IKnmTypeSelectOption | undefined;
  kindKnmFieldValue: IDictionaryKnmKindSelectOption | undefined;
  onApply: (changePlanReasonAfterConfirm: IKnmErknmFormChangePlanReason[]) => void;
  onClose: () => void;
  objectsErknm?: IKnmErknmFormObject[];
}

interface IChangeKnmModalStoreProps {
  knmFormRef: IKnmFormRef | undefined;
  knmPlanReasonChangeTypesOptions: IKnmReasonChangeTypeSelectOption[];
}

const ChangeKnmModalWithoutStore = ({
  visible,
  typeFieldValue,
  kindKnmFieldValue,
  onApply,
  onClose,
  knmFormRef,
  knmPlanReasonChangeTypesOptions,
  objectsErknm,
}: IChangeKnmModalProps & IChangeKnmModalStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const formRef = useRef<ChangeKnmModalFormRenderProps['form']>();
  const knmPlanReasonChangeFilteredOptions = useMemo(
    () =>
      knmPlanReasonChangeTypesOptions.filter(option => option.operation === KnmReasonChangeTypeOperationEnum.CHANGE),
    [knmPlanReasonChangeTypesOptions],
  );

  const isCheckControlObjectReasonValidateError1 = (changeTypeCode: string | undefined) => {
    return (
      changeTypeCode === 'RSN_CHNG_PP_XII' &&
      kindKnmFieldValue?.code === 'RO' &&
      !objectsErknm?.some(object => object.objectType?.code === '3')
    );
  };

  const isCheckControlObjectReasonValidateError2 = (changeTypeCode: string | undefined) => {
    return (
      changeTypeCode === 'RSN_CHNG_PP_XII' &&
      kindKnmFieldValue?.code !== 'RO' &&
      objectsErknm?.some(object => object.objectType?.code !== '3')
    );
  };

  const handleReasonChange = useCallback(() => {
    if (formRef.current) {
      formRef.current.change('documents', []);
    }
  }, []);

  useEffect(() => {
    if (visible) {
      dispatch(catalogGet(CommonCatalogsNamesEnum.KNM_PLAN_REASON_CHANGE_TYPES));
    }
  }, [dispatch, visible]);

  const handleAfterClose = useCallback(() => {
    formRef.current && formRef.current.reset();
  }, []);

  const handleFormSubmit = useCallback(
    (values: IChangeKnmModalForm) => {
      const currentValues = knmFormRef?.current.getState().values;

      if (values.changePlanReason) {
        knmFormRef?.current.change('changePlanReason', [
          ...(currentValues?.changePlanReason || []),
          ...values.changePlanReason,
        ]);
      }
      if (values.documents) {
        knmFormRef?.current.change('documents', [...(currentValues?.documents || []), ...values.documents]);
      }

      const isNeedCheckChangesForUpdate = values.changePlanReason?.some(
        item =>
          item?.reasonChangeType?.code === 'RSN_CHNG_PP_IX' ||
          item?.reasonChangeType?.code === 'RSN_CHNG_PP_X' ||
          item?.reasonChangeType?.code === 'RSN_CHNG_PP_XI',
      );

      const isNeedChangeStatusKnm = values.changePlanReason?.some(
        item => item?.reasonChangeType?.code === 'RSN_CHNG_PP_XXII',
      );

      if (isNeedCheckChangesForUpdate) {
        dispatch(knmCheckChanges());
        knmFormRef?.current.submit();
      }

      if (isNeedChangeStatusKnm) {
        dispatch(setIsNpaStructureChanged(true));
      }

      dispatch(knmSetIsChangePlanReasonActive(true));
      onApply(values.changePlanReason!);
    },
    [dispatch, knmFormRef, onApply],
  );

  function renderForm({ handleSubmit, form, values }: ChangeKnmModalFormRenderProps) {
    formRef.current = form;

    const isCheckControlObjectReasonValidateHasError = values.changePlanReason
      ? values.changePlanReason.some(changePlanReason => {
          return (
            isCheckControlObjectReasonValidateError1(changePlanReason.reasonChangeType?.code) ||
            isCheckControlObjectReasonValidateError2(changePlanReason.reasonChangeType?.code)
          );
        })
      : false;

    const isSubmitButtonDisabled =
      !values.changePlanReason?.length ||
      !values.documents?.length ||
      !values.documents?.every(document => document?.attachments?.length) ||
      isCheckControlObjectReasonValidateHasError;

    const { push, remove } = form.mutators;

    function addChangePlanReason() {
      const addingChangePlanReason: IKnmErknmFormChangePlanReason = {
        code: undefined,
        reasonChangeType: undefined,
        guid: undefined,
        dateCreate: undefined,
        listId: uuidv4(),
        isNew: true,
      };

      push('changePlanReason', addingChangePlanReason);
    }

    const makeRemoveEventHandler = (index: number) => () => remove?.('changePlanReason', index);
    const formValues = form.getState().values;
    const firstReasonCode = formValues.changePlanReason?.[0]?.reasonChangeType?.code;

    return (
      <form onSubmit={handleSubmit}>
        <ModalHeader title="Причины изменения и документы" />
        <ModalBody border="bottom">
          <div className="shared-title">
            Причины&nbsp;
            <span className="shared-no-wrap-text">
              изменения
              <Button
                className="shared-btn-add"
                text="Добавить"
                variant="link"
                color="primary"
                type="button"
                onClick={addChangePlanReason}
              />
            </span>
          </div>

          <FieldArray name="changePlanReason">
            {({ fields }: FieldArrayRenderProps<IKnmErknmFormChangePlanReason, HTMLElement>) => {
              if (!fields.length) {
                return null;
              }

              return (
                <ul className={styles.ChangePlanReasonList}>
                  {fields.map((fieldName, index) => {
                    const fieldValue: IKnmErknmFormChangePlanReason = fields.value?.[index];

                    return (
                      <>
                        <li className="shared-field-row_padding" key={fieldValue.listId}>
                          <SelectInputField
                            className="shared-deleting-row"
                            name={`${fieldName}.reasonChangeType`}
                            placeholder="Выберите причину изменения"
                            options={knmPlanReasonChangeFilteredOptions}
                            size="medium"
                            validate={makeRequired('Поле не может быть пустым')}
                            onChange={() => {
                              handleReasonChange();
                            }}
                          />
                          <CloseButton className="shared-deleting-row-btn" onClick={makeRemoveEventHandler(index)} />
                        </li>
                        {(isCheckControlObjectReasonValidateError1(fieldValue?.reasonChangeType?.code) ||
                          isCheckControlObjectReasonValidateError2(fieldValue?.reasonChangeType?.code)) && (
                          <div className={styles.ValidationErrorText}>
                            {'Паспорт мероприятия не содержит объектов с типом «Производственные объекты»'}
                          </div>
                        )}
                      </>
                    );
                  })}
                </ul>
              );
            }}
          </FieldArray>

          <KnmDocuments
            formInstance={form}
            title="Документы"
            typeErknm={typeFieldValue}
            kindKnm={kindKnmFieldValue}
            reasonDocument
            reasonChangeCode={firstReasonCode}
          />
        </ModalBody>
        <ModalActions>
          <Button text="Подтвердить" color="primary" size="medium" type="submit" disabled={isSubmitButtonDisabled} />
        </ModalActions>
      </form>
    );
  }

  return (
    <Modal visible={visible} modalBodyClassName={styles.ModalBody} onClose={onClose} afterClose={handleAfterClose}>
      <Form<IChangeKnmModalForm> render={renderForm} mutators={{ ...arrayMutators }} onSubmit={handleFormSubmit} />
    </Modal>
  );
};

const mapStateToProps = (state: IRootState): IChangeKnmModalStoreProps => ({
  knmFormRef: getKnmFormRef(state),
  knmPlanReasonChangeTypesOptions: geCatalogsKnmPlanReasonChangeTypesSelectOptions(state),
});

export const ChangeKnmModal = connect(mapStateToProps)(ChangeKnmModalWithoutStore);
